import React, { useCallback } from 'react';
import styles from './style.module.scss';
import modalStyles from '../modal/style.module.scss';
import Image from 'next/image';
import Link from 'next/link';
import Button, { PRIMARY_THEME } from 'components/control/button';
import CloseIconExpForm from 'components/icon/close-icon-exp-form';
import { buildClassName } from 'utils/build-class-name';
import { PowerOfExpData } from '@tenants/types/tenant';
import { useTranslation } from 'next-i18next';

const DEFAULT_IMG_WIDTH = 342;
const DEFAULT_IMG_HEIGHT = 273;

export type PresentationItem = {
  title: string;
  image: PresentationItemImage;
  link: PresentationItemLink;
};

export type PresentationItemLink = {
  label: string;
  link?: string;
  target?: string;
  isAnchor?: boolean;
};

export type PresentationItemImage = {
  src: string;
  alt: string;
  width?: number | `${number}` | undefined;
  height?: number | `${number}` | undefined;
};

export interface PresentationModalData {
  title: string;
  presentations: PresentationItem[];
}

interface PresentationItemProps {
  item: PresentationItem;
  index: number;
}

const PresentationItemComponent = ({ item, index }: PresentationItemProps) => {
  const renderLink = (link: PresentationItemLink, index: number) => {
    if (!link.link) {
      return (
        <Button key={`presentation-${index}`} className={styles.button} label={link.label} theme={PRIMARY_THEME} />
      );
    }

    const target = link.target || '_blank';
    
    return (link.isAnchor ?
      <a key={`presentation-${index}`} href={link.link} target={target} rel="noopener noreferrer">
        <Button className={styles.button} label={link.label} theme={PRIMARY_THEME} />
      </a>
      :
      <Link key={`presentation-${index}`} href={link.link} target={target}>
        <Button className={styles.button} label={link.label} theme={PRIMARY_THEME} />
      </Link>
    );
  };

  return (
    <div className={styles.column}>
      <h1 className={styles.location}>{item.title}</h1>
      <div className={styles['image-wrapper']}>
        <Image 
          src={item.image.src} 
          alt={item.image.alt} 
          width={item.image.width || DEFAULT_IMG_WIDTH} 
          height={item.image.height || DEFAULT_IMG_HEIGHT} 
          style={{ maxWidth: '100%', height: 'auto' }}
        />
      </div>
      {renderLink(item.link, index)}
    </div>
  );
};

type PowerOfExpModalProps = {
  onClose: () => void;
  powerOfExpData: PowerOfExpData;
};

export default function PowerOfExpModal({ onClose, powerOfExpData }: PowerOfExpModalProps) {
  const { t } = useTranslation();
  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);



  const presentations = powerOfExpData.presentation.modal.regions.map(region => ({
    title: t(region.title),
    image: {
      src: t(region.imageSrc),
      alt: t(region.title),
    },
    link: {
      label: t(powerOfExpData.presentation.modal.downloadButton),
      link: t(region.url),
      target: '_blank',
      isAnchor: true,
    },
  }));

  return (
    <div className={modalStyles.component} onClick={handleBackdropClick}>
      <div className={modalStyles.modal}>
        <div className={buildClassName(modalStyles['close-container'], styles['close-container'])} onClick={onClose}>
          <CloseIconExpForm className={styles.close} />
        </div>
        <div className={modalStyles.content}>
          <div className={styles.component}>
            <h1 className={styles.heading}>{t(powerOfExpData.presentation.modal.title)}</h1>
            <div className={styles['three-columns']}>
              {presentations.map((item, index) => (
                <PresentationItemComponent key={index} item={item} index={index} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}